/**
 * Formatted Text Block Component
 * Displays formatted text with font size from shared context
 */

import React, { useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { TextFormattingConfig } from '../admin/RichTextEditor';
import { LEARNER_UI_PERSIAN_STACK, type LessonTypographyScope } from '../../constants/learnerPersianFonts';
import { getLessonRichTextLearnerCss } from '../../utils/lessonRichTextStyles';
import { sanitizeLessonHtml } from '../../utils/sanitizeWordPasteHtml';
import { injectHighlightMarks, renderHighlightedText } from '../../utils/searchHighlight';
import {
  injectStickyAudioMarks,
  renderTextWithStickyAudio,
  StickyAudioMap
} from '../../utils/stickyAudio';
import { StickyAudioInteractionLayer } from '../../contexts/StickyAudioContext';

interface FormattedTextBlockProps {
  textContent: string;
  formatting?: TextFormattingConfig;
  tips?: Array<{ id: string; description: string; textRange?: { start: number; end: number } }>;
  renderTipIcon?: (tip: any, position: string) => React.ReactNode;
  /**
   * When set, every occurrence of this substring inside the rendered body (HTML
   * or plain text) is wrapped with `<mark class="search-result-mark">`. Used by
   * the global learner search to point the eye at the matched word.
   */
  searchHighlightTerm?: string;
  /** Learner lesson Text / Text+Audio: scoped face + size only. */
  lessonTypography?: LessonTypographyScope;
  /** Global sticky-audio phrase map; when enabled, matching text is underlined. */
  stickyAudioMap?: StickyAudioMap;
  stickyAudioEnabled?: boolean;
  /** Keeps this block's all-words / important-words underlines independent. */
  wordHelpBlockId?: number;
  /** Forces block direction (e.g. English-locked quiz/exercise in FA UI). */
  contentDirection?: 'ltr' | 'rtl';
}

export const FormattedTextBlock: React.FC<FormattedTextBlockProps> = ({
  textContent,
  formatting,
  tips = [],
  renderTipIcon,
  searchHighlightTerm,
  lessonTypography,
  stickyAudioMap,
  stickyAudioEnabled = false,
  wordHelpBlockId,
  contentDirection,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mult = lessonTypography?.multiplier ?? 1;
  const lessonStack = lessonTypography?.stack ?? LEARNER_UI_PERSIAN_STACK;
  const [isDark, setIsDark] = React.useState(false);

  // Parse formatting config early — direction comes from authored HTML, not UI locale.
  const config: TextFormattingConfig = formatting || {};
  const baseFontSize = config.baseFontSize || '16px';
  const formattedContent = config.formattedContent;
  const renderedHtml = React.useMemo(
    () => (formattedContent ? sanitizeLessonHtml(formattedContent) : undefined),
    [formattedContent]
  );
  const hasAuthoredHtml = Boolean(renderedHtml);
  /** Quiz/exercise English-lock only; lesson/book blocks omit this prop. */
  const forcedDirection = contentDirection;
  const directionStyle: React.CSSProperties | undefined = forcedDirection
    ? {
        direction: forcedDirection,
        textAlign: forcedDirection === 'ltr' ? 'left' : 'right',
      }
    : undefined;
  const rootDir: 'ltr' | 'rtl' | 'auto' | undefined =
    forcedDirection ?? (!hasAuthoredHtml ? 'auto' : undefined);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark') || 
                window.matchMedia('(prefers-color-scheme: dark)').matches);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const hasStickyPhrases = Boolean(
    stickyAudioMap && Object.keys(stickyAudioMap).length > 0
  );
  const stickyPhraseLayerClass = hasStickyPhrases ? 'sticky-audio-phrase-layer' : '';
  const stickyContentKey = React.useMemo(
    () =>
      `${textContent ?? ''}\0${formattedContent ?? ''}\0${Object.keys(stickyAudioMap ?? {}).sort().join(',')}`,
    [textContent, formattedContent, stickyAudioMap]
  );

  const applyStickyAndSearch = React.useCallback(
    (html: string) => {
      let out = html;
      if (hasStickyPhrases && stickyAudioMap) {
        out = injectStickyAudioMarks(out, stickyAudioMap);
      }
      return injectHighlightMarks(out, searchHighlightTerm);
    },
    [hasStickyPhrases, stickyAudioMap, searchHighlightTerm]
  );
  // Note: textAlign is no longer used globally - alignment is per-paragraph in HTML
  
  // Debug: Log what FormattedTextBlock receives
  React.useEffect(() => {
    if (formatting) {
      const hasHighlights = formattedContent?.includes('text-audio-highlight') || false;
      console.log('FormattedTextBlock: Received formatting:', {
        hasFormattedContent: !!formattedContent,
        formattedContentLength: formattedContent?.length,
        formattedContentPreview: formattedContent?.substring(0, 100),
        textContentLength: textContent?.length,
        hasHighlights: hasHighlights,
        highlightCount: hasHighlights ? (formattedContent.match(/text-audio-highlight/g) || []).length : 0
      });
    } else {
      console.warn('FormattedTextBlock: No formatting provided, will render plain text');
    }
  }, [formatting, formattedContent, textContent, config]);
  
  // Generate unique ID for this component instance to scope styles
  // Use useRef to ensure stable ID across re-renders - this prevents tip icons from closing
  const styleIdRef = React.useRef<string | null>(null);
  if (!styleIdRef.current) {
    // Generate ID once on mount, keep it stable across re-renders
    styleIdRef.current = `formatted-text-${Math.random().toString(36).substring(2, 11)}`;
  }
  const styleId = styleIdRef.current;

  // Calculate current font size
  const baseSize = parseFloat(baseFontSize);
  const currentFontSize = `${baseSize * mult}px`;

  // Filter tips for text ranges (stable ref while `tips` prop is stable)
  const textRangeTips = React.useMemo(() => tips.filter((tip) => tip.textRange), [tips]);

  const sortedTips = React.useMemo(
    () =>
      [...textRangeTips].sort(
        (a, b) => (a.textRange?.start || 0) - (b.textRange?.start || 0)
      ),
    [textRangeTips]
  );

  // Render text with tips
  const renderTextWithTips = () => {
    console.log('FormattedTextBlock renderTextWithTips:', {
      textRangeTipsLength: textRangeTips.length,
      hasFormattedContent: !!formattedContent,
      formattedContent: formattedContent
    });
    
    if (textRangeTips.length === 0) {
      // No tips, render formatted content or plain text
      if (renderedHtml) {
        console.log('FormattedTextBlock: Rendering formatted content with HTML');
        return (
          <>
            <style dangerouslySetInnerHTML={{ __html: `
              ${getLessonRichTextLearnerCss(`#${styleId}`, lessonStack)}
            `}} />
            <div
              id={styleId}
              dangerouslySetInnerHTML={{ __html: applyStickyAndSearch(renderedHtml) }}
              style={{
                fontSize: currentFontSize,
                lineHeight: '1.6',
                ...directionStyle,
              }}
            />
            <StickyAudioInteractionLayer
              containerRef={containerRef}
              enabled={Boolean(stickyAudioEnabled && stickyAudioMap && Object.keys(stickyAudioMap).length > 0)}
              contentKey={stickyContentKey}
              {...(wordHelpBlockId != null ? { blockId: wordHelpBlockId } : {})}
            />
          </>
        );
      }
      console.log('FormattedTextBlock: No formattedContent, rendering plain text');
      const plainBody = hasStickyPhrases
        ? renderTextWithStickyAudio(textContent, stickyAudioMap!)
        : searchHighlightTerm
          ? renderHighlightedText(textContent, searchHighlightTerm)
          : textContent;
      return (
        <>
          <p 
            style={{
              fontSize: currentFontSize,
              lineHeight: '1.6',
              ...directionStyle,
            }}
            className="whitespace-pre-wrap"
          >
            {plainBody}
          </p>
          <StickyAudioInteractionLayer
            containerRef={containerRef}
            enabled={Boolean(stickyAudioEnabled && stickyAudioMap && Object.keys(stickyAudioMap).length > 0)}
            contentKey={stickyContentKey}
            {...(wordHelpBlockId != null ? { blockId: wordHelpBlockId } : {})}
          />
        </>
      );
    }
    
    console.log('FormattedTextBlock: Has textRangeTips, rendering formatted content with tips');

    // Has tips - render like preview: split text and insert React tip components
    // But preserve formatting by inserting HTML at the right positions

    // Helper function to insert tip marker at a specific text position in HTML
    // This preserves formatting tags (b, u, font, span) around the text
    const insertTipMarker = (html: string, start: number, end: number, tipId: string): string => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Walk through text nodes to find the position
      let currentPos = 0;
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let targetNode: Text | null = null;
      let targetNodeStart = 0;
      
      while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        const nodeText = node.textContent || '';
        const nodeStart = currentPos;
        const nodeEnd = currentPos + nodeText.length;
        
        if (start >= nodeStart && start < nodeEnd) {
          targetNode = node;
          targetNodeStart = nodeStart;
          break;
        }
        currentPos = nodeEnd;
      }
      
      if (targetNode && targetNode.parentNode) {
        const nodeText = targetNode.textContent || '';
        const relativeStart = start - targetNodeStart;
        const relativeEnd = Math.min(end - targetNodeStart, nodeText.length);
        
        // Get the parent element to preserve formatting (could be b, u, span, font, etc.)
        const parentElement = targetNode.parentElement;
        if (!parentElement) return tempDiv.innerHTML;
        
        // Split the text node
        const beforeText = nodeText.substring(0, relativeStart);
        const tipText = nodeText.substring(relativeStart, relativeEnd);
        const afterText = nodeText.substring(relativeEnd);
        
        // Helper to wrap text in the same formatting as the parent
        const wrapWithParentFormatting = (text: string, parent: HTMLElement): Node => {
          if (!text) return document.createTextNode('');
          
          // If parent is a formatting element (b, u, span, font), wrap text in it
          const tagName = parent.tagName.toLowerCase();
          if (tagName === 'b' || tagName === 'strong' || tagName === 'u' || tagName === 'span' || tagName === 'font') {
            const wrapper = parent.cloneNode(false) as HTMLElement;
            wrapper.textContent = text;
            return wrapper;
          }
          
          // Otherwise just return text node
          return document.createTextNode(text);
        };
        
        // Create fragments for before, tip, and after text
        const fragment = document.createDocumentFragment();
        
        // Insert before text with formatting
        if (beforeText) {
          fragment.appendChild(wrapWithParentFormatting(beforeText, parentElement));
        }
        
        // Create tip wrapper - position relative so absolute icon positioning works
        const tipSpan = document.createElement('span');
        tipSpan.className = 'relative inline';
        tipSpan.setAttribute('data-tip-id', tipId);
        // Make it relative so absolutely positioned icon is positioned relative to this span
        tipSpan.style.position = 'relative';
        tipSpan.style.display = 'inline';
        // Don't set inline font-size - let CSS handle it to ensure it matches parent
        // This prevents the tip span from affecting adjacent text
        
        // Wrap tip text in the same formatting as parent
        // The selected text should keep normal font-size (not 0.5rem)
        // Only the tip tooltip content should be 0.5rem
        let formattedTipText = wrapWithParentFormatting(tipText, parentElement);
        // Don't set font-size - let it inherit from parent to keep normal size
        tipSpan.appendChild(formattedTipText);
        
        fragment.appendChild(tipSpan);
        
        // Insert after text with formatting
        if (afterText) {
          fragment.appendChild(wrapWithParentFormatting(afterText, parentElement));
        }
        
        // Replace the original text node with the fragment
        targetNode.parentNode?.replaceChild(fragment, targetNode);
      }
      
      return tempDiv.innerHTML;
    };

    // If we have formattedContent, we need to insert tips while preserving HTML formatting
    // Use the same approach as preview but with formatted HTML
    if (renderedHtml) {
      let modifiedHtml = renderedHtml;
      
      // Insert markers from end to start to preserve positions
      const sortedTipsDesc = [...sortedTips].sort((a, b) => 
        (b.textRange?.start || 0) - (a.textRange?.start || 0)
      );
      
      sortedTipsDesc.forEach((tip) => {
        const start = tip.textRange?.start || 0;
        const end = tip.textRange?.end || 0;
        modifiedHtml = insertTipMarker(modifiedHtml, start, end, tip.id);
      });
      
      // Render HTML with tip markers, then replace markers with React components
      return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
            ${getLessonRichTextLearnerCss(`#${styleId}`, lessonStack)}
            #${styleId} {
              font-size: ${currentFontSize} !important;
            }
            #${styleId} span[data-tip-id] {
              position: relative;
              display: inline-flex;
              align-items: center;
              gap: 0;
            }
          `}} />
          {/*
            Tip portals must unmount before the HTML node is replaced. Search
            highlighting swaps `dangerouslySetInnerHTML` and detaches the old
            wrappers; portaling into those nodes throws and the page error
            boundary replaces the focused result.
          */}
          <TipIconRenderer
            tips={sortedTips}
            {...(renderTipIcon ? { renderTipIcon } : {})}
            containerId={styleId}
            htmlVersion={`${searchHighlightTerm ?? ''}|${modifiedHtml.length}|${stickyContentKey}`}
          />
          <div
            id={styleId}
            dangerouslySetInnerHTML={{ __html: applyStickyAndSearch(modifiedHtml) }}
            style={{
              fontSize: currentFontSize,
              lineHeight: '1.6',
              ...directionStyle,
            }}
          />
          <StickyAudioInteractionLayer
            containerRef={containerRef}
            enabled={Boolean(stickyAudioEnabled && stickyAudioMap && Object.keys(stickyAudioMap).length > 0)}
            contentKey={stickyContentKey}
            {...(wordHelpBlockId != null ? { blockId: wordHelpBlockId } : {})}
          />
        </>
      );
    }
    
    // Fallback: No formattedContent, render plain text with tips (like preview)

    // Fallback: No formattedContent, render plain text with tips (original logic)
    const plainText = typeof textContent === 'string' ? textContent : '';
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    sortedTips.forEach((tip) => {
      const start = tip.textRange?.start || 0;
      const end = tip.textRange?.end || 0;

      // Add text before this tip
      if (start > lastIndex) {
        const beforeText = plainText.substring(lastIndex, start);
        elements.push(
          <span key={`text-${lastIndex}-${start}`}>
            {searchHighlightTerm ? renderHighlightedText(beforeText, searchHighlightTerm) : beforeText}
          </span>
        );
      }

      // Add text with tip icon
      // The selected text should keep normal font-size (not 0.5rem)
      // Only the tip tooltip content should be 0.5rem
      const highlightedText = plainText.substring(start, end);
      elements.push(
        <span key={`tip-${tip.id}`} className="relative inline" style={{ position: 'relative', display: 'inline' }}>
          <span>{searchHighlightTerm ? renderHighlightedText(highlightedText, searchHighlightTerm) : highlightedText}</span>
          {renderTipIcon && (
            <span style={{
              position: 'absolute',
              top: '-5px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              pointerEvents: 'auto',
              fontSize: '0.7rem', // Smaller icon
              opacity: 0.6 // More transparent
            }}>
              {renderTipIcon(tip, 'top')}
            </span>
          )}
        </span>
      );

      lastIndex = end;
    });

    // Add remaining text
    if (lastIndex < plainText.length) {
      const tail = plainText.substring(lastIndex);
      elements.push(
        <span key={`text-${lastIndex}-end`}>
          {searchHighlightTerm ? renderHighlightedText(tail, searchHighlightTerm) : tail}
        </span>
      );
    }

    return (
      <p 
        style={{
          fontSize: currentFontSize,
          lineHeight: '1.6',
          ...directionStyle,
        }}
        className="whitespace-pre-wrap"
      >
        {elements}
      </p>
    );
  };

  return (
    <div
      ref={containerRef}
      dir={rootDir}
      className={[
        'formatted-text-block-root max-w-none',
        hasAuthoredHtml
          ? 'formatted-text-block-root--authored'
          : 'formatted-text-block-root--plain',
        forcedDirection === 'ltr' ? 'formatted-text-block-root--forced-ltr' : '',
        forcedDirection === 'rtl' ? 'formatted-text-block-root--forced-rtl' : '',
        stickyPhraseLayerClass,
        stickyAudioEnabled && hasStickyPhrases ? 'sticky-audio-underlines-active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {renderTextWithTips()}
    </div>
  );
};

// Helper component to insert tip icons into the rendered HTML
const TipIconInserter: React.FC<{
  tips: Array<{ id: string; description: string; textRange?: { start: number; end: number } }>;
  renderTipIcon?: (tip: any, position: string) => React.ReactNode;
  containerId: string;
}> = ({ tips, renderTipIcon, containerId }) => {
  const iconRootsRef = useRef<Map<string, Root>>(new Map());
  const insertedIconsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    console.log(`TipIconInserter: Effect running for container ${containerId} with ${tips.length} tips`);
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`TipIconInserter: Container with id "${containerId}" not found`);
      return;
    }
    if (!renderTipIcon) {
      console.warn('TipIconInserter: renderTipIcon not available');
      return;
    }

    // Define insertIcon function first
    const insertIcon = (tip: { id: string; description: string; textRange?: { start: number; end: number } }, tipSpan: HTMLElement) => {
      // Check if icon already inserted
      if (tipSpan.querySelector('.tip-icon-wrapper') || insertedIconsRef.current.has(tip.id)) {
        return;
      }

      console.log(`TipIconInserter: Inserting icon for tip ${tip.id}`);
      
      // Create icon container - absolutely positioned on top-middle of the word, smaller size
      const iconWrapper = document.createElement('span');
      iconWrapper.className = 'tip-icon-wrapper';
      // Position absolutely on top-middle, doesn't take up space in text flow
      iconWrapper.style.position = 'absolute';
      iconWrapper.style.top = '-5px';
      iconWrapper.style.left = '50%';
      iconWrapper.style.transform = 'translateX(-50%)';
      iconWrapper.style.zIndex = '10';
      iconWrapper.style.pointerEvents = 'auto';
      iconWrapper.style.fontSize = '0.7rem'; // Smaller icon
      iconWrapper.style.opacity = '0.6'; // More transparent
      
      try {
        // Create React root and render icon
        const root = createRoot(iconWrapper);
        iconRootsRef.current.set(tip.id, root);
        
        const iconElement = renderTipIcon(tip, 'top');
        if (iconElement) {
          // Append to DOM FIRST
          tipSpan.appendChild(iconWrapper);
          insertedIconsRef.current.add(tip.id);
          
          // Try rendering with React - but also create a fallback HTML button
          // in case React rendering fails due to context issues
          const createFallbackButton = () => {
            const button = document.createElement('button');
            button.className = 'inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-yellow-100/50 dark:bg-yellow-900/15 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 transition-all duration-200 cursor-pointer opacity-60 hover:opacity-100';
            button.setAttribute('aria-label', 'Show tip');
            button.innerHTML = '<svg class="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>';
            
            // Add click handler to show tip
            button.onclick = (e) => {
              e.stopPropagation();
              // Create and show tooltip
              const tooltip = document.createElement('div');
              tooltip.className = 'fixed z-[100] p-4 rounded-lg shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700';
              tooltip.style.left = `${e.clientX}px`;
              tooltip.style.top = `${e.clientY}px`;
              tooltip.textContent = tip.description || 'Tip';
              document.body.appendChild(tooltip);
              
              // Remove tooltip on click outside
              const removeTooltip = () => {
                document.body.removeChild(tooltip);
                document.removeEventListener('click', removeTooltip);
              };
              setTimeout(() => document.addEventListener('click', removeTooltip), 100);
            };
            
            return button;
          };
          
          // Try React rendering first
          try {
            root.render(<>{iconElement}</>);
            
            // Check if React actually rendered after a delay
            setTimeout(() => {
              const hasReactContent = iconWrapper.querySelector('button') !== null;
              if (!hasReactContent) {
                console.warn(`TipIconInserter: React didn't render, using fallback for tip ${tip.id}`);
                // React didn't render, use fallback
                iconWrapper.innerHTML = '';
                iconWrapper.appendChild(createFallbackButton());
              } else {
                console.log(`TipIconInserter: React successfully rendered icon for tip ${tip.id}`);
              }
            }, 100);
          } catch (error) {
            console.error(`TipIconInserter: Error rendering with React for tip ${tip.id}:`, error);
            // Use fallback
            iconWrapper.innerHTML = '';
            iconWrapper.appendChild(createFallbackButton());
          }
          
          // Verify icon is actually in DOM and visible - check multiple times as React may need time to render
          const checkIcon = (attempt: number = 0) => {
            const insertedIcon = tipSpan.querySelector('.tip-icon-wrapper');
            if (insertedIcon) {
              const computedStyle = window.getComputedStyle(insertedIcon);
              const hasContent = insertedIcon.children.length > 0 || insertedIcon.textContent?.trim().length > 0;
              const width = computedStyle.width;
              const height = computedStyle.height;
              
              // Check the button inside
              const button = insertedIcon.querySelector('button');
              const buttonStyle = button ? window.getComputedStyle(button) : null;
              
              console.log(`TipIconInserter: Icon check for tip ${tip.id} (attempt ${attempt + 1})`, {
                inDOM: !!insertedIcon,
                display: computedStyle.display,
                visibility: computedStyle.visibility,
                opacity: computedStyle.opacity,
                width,
                height,
                hasChildren: insertedIcon.children.length > 0,
                hasContent,
                innerHTML: insertedIcon.innerHTML.substring(0, 300),
                firstChild: insertedIcon.firstElementChild?.tagName,
                hasButton: !!button,
                buttonDisplay: buttonStyle?.display,
                buttonVisibility: buttonStyle?.visibility,
                buttonOpacity: buttonStyle?.opacity,
                buttonWidth: buttonStyle?.width,
                buttonHeight: buttonStyle?.height
              });
              
              // If width is still 0 and we haven't tried too many times, check again
              if ((width === '0px' || !hasContent) && attempt < 3) {
                setTimeout(() => checkIcon(attempt + 1), 100);
              } else if (width === '0px' || !hasContent) {
                console.error(`TipIconInserter: Icon still has 0 width or no content after ${attempt + 1} attempts for tip ${tip.id}`);
              }
            } else {
              console.error(`TipIconInserter: Icon wrapper not found in DOM after insertion for tip ${tip.id}`);
            }
          };
          
          setTimeout(() => checkIcon(0), 50);
          setTimeout(() => checkIcon(1), 200);
          setTimeout(() => checkIcon(2), 500);
          
          console.log(`TipIconInserter: Successfully inserted icon for tip ${tip.id}`);
        } else {
          console.warn(`TipIconInserter: renderTipIcon returned null for tip ${tip.id}`);
        }
      } catch (error) {
        console.error(`TipIconInserter: Error creating root for tip ${tip.id}:`, error);
      }
    };

    // Use MutationObserver to re-insert icons if they're removed by React re-renders
    let reinsertTimeout: NodeJS.Timeout | null = null;
    let isReinserting = false;
    const observer = new MutationObserver((mutations) => {
      // Skip if we're already re-inserting to avoid loops
      if (isReinserting) return;
      
      // Debounce re-insertion to avoid infinite loops
      if (reinsertTimeout) {
        clearTimeout(reinsertTimeout);
      }
      
      reinsertTimeout = setTimeout(() => {
        isReinserting = true;
        // Check if any tip spans lost their icons
        let needsReinsertion = false;
        tips.forEach((tip) => {
          const tipSpan = container.querySelector(`span[data-tip-id="${tip.id}"]`) as HTMLElement;
          if (tipSpan && !tipSpan.querySelector('.tip-icon-wrapper')) {
            // Icon was removed, re-insert it
            needsReinsertion = true;
            // Remove from inserted set so it can be re-inserted
            insertedIconsRef.current.delete(tip.id);
            insertIcon(tip, tipSpan);
          }
        });
        if (needsReinsertion) {
          console.log('TipIconInserter: Re-inserted icons after DOM mutation');
        }
        isReinserting = false;
      }, 150);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: false
    });

    // Initial insertion - try multiple times to ensure icons are inserted
    const attemptInsertion = (attempt: number = 0) => {
      const maxAttempts = 5;
      if (attempt >= maxAttempts) {
        console.warn('TipIconInserter: Max insertion attempts reached');
        return;
      }

      // Find all tip spans and insert icons
      let allFound = true;
      let insertedCount = 0;
      tips.forEach((tip) => {
        const tipSpan = container.querySelector(`span[data-tip-id="${tip.id}"]`) as HTMLElement;
        if (tipSpan) {
          if (!tipSpan.querySelector('.tip-icon-wrapper')) {
            insertIcon(tip, tipSpan);
            insertedCount++;
          } else {
            console.log(`TipIconInserter: Icon already exists for tip ${tip.id}`);
          }
        } else {
          allFound = false;
          console.warn(`TipIconInserter: Could not find span with data-tip-id="${tip.id}" on attempt ${attempt + 1}`);
        }
      });
      console.log(`TipIconInserter: Attempt ${attempt + 1}: Found ${tips.length - (allFound ? 0 : 1)} spans, inserted ${insertedCount} icons`);

      // If not all found and we haven't reached max attempts, try again
      if (!allFound && attempt < maxAttempts - 1) {
        setTimeout(() => attemptInsertion(attempt + 1), 100 * (attempt + 1));
      }
    };

    // Start with immediate attempt, then retry with delays
    attemptInsertion(0);
    const timeoutId1 = setTimeout(() => attemptInsertion(1), 100);
    const timeoutId2 = setTimeout(() => attemptInsertion(2), 300);

    // Cleanup - use setTimeout to avoid unmounting during render
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      if (reinsertTimeout) {
        clearTimeout(reinsertTimeout);
      }
      observer.disconnect();
      
      // Defer unmounting to avoid React render conflicts
      setTimeout(() => {
        iconRootsRef.current.forEach((root) => {
          try {
            root.unmount();
          } catch (error) {
            // Ignore errors during unmount - root may already be unmounted
            console.debug('TipIconInserter: Root already unmounted or error during cleanup:', error);
          }
        });
        iconRootsRef.current.clear();
        insertedIconsRef.current.clear();
      }, 0);
    };
  }, [tips, renderTipIcon, containerId]);

  return null;
};

// Component to render tip icons using React Portal for full functionality
const TipIconRenderer: React.FC<{
  tips: Array<{ id: string; description: string; textRange?: { start: number; end: number } }>;
  renderTipIcon?: (tip: any, position: string) => React.ReactNode;
  containerId: string;
  /** Bumped whenever the parent's `dangerouslySetInnerHTML` content changes so
   *  the wrapper-creation effect re-runs and re-discovers the (now rebuilt)
   *  `data-tip-id` spans. Keeps tip icons visible across global-search
   *  highlight toggles. */
  htmlVersion?: string;
}> = ({ tips, renderTipIcon, containerId, htmlVersion }) => {
  const iconContainersRef = React.useRef<Map<string, HTMLElement>>(new Map());
  /** `null` until wrappers exist for the current HTML. Portals stay unmounted
   *  on the render that swaps the highlighted markup. */
  const [portalVersion, setPortalVersion] = React.useState<string | null>(null);
  const tipsKey = React.useMemo(() => tips.map((t) => t.id).join(','), [tips]);
  const versionKey = htmlVersion ?? '';

  React.useEffect(() => {
    iconContainersRef.current.clear();

    const container = document.getElementById(containerId);
    if (container) {
      tips.forEach((tip) => {
        const tipSpan = container.querySelector(`span[data-tip-id="${tip.id}"]`) as HTMLElement;
        if (tipSpan) {
          let iconWrapper = tipSpan.querySelector('.tip-icon-wrapper') as HTMLElement;
          if (!iconWrapper) {
            iconWrapper = document.createElement('span');
            iconWrapper.className = 'tip-icon-wrapper';
            iconWrapper.style.position = 'absolute';
            iconWrapper.style.top = '-5px';
            iconWrapper.style.left = '50%';
            iconWrapper.style.transform = 'translateX(-50%)';
            iconWrapper.style.zIndex = '10';
            iconWrapper.style.pointerEvents = 'auto';
            iconWrapper.style.fontSize = '0.7rem';
            iconWrapper.style.opacity = '0.6';
            tipSpan.appendChild(iconWrapper);
          }
          iconContainersRef.current.set(tip.id, iconWrapper);
        }
      });
    }

    setPortalVersion(versionKey);

    return () => {
      iconContainersRef.current.clear();
    };
  }, [tipsKey, containerId, versionKey, tips]);

  if (portalVersion !== versionKey || !renderTipIcon) return null;

  return (
    <>
      {tips.map((tip) => {
        const container = iconContainersRef.current.get(tip.id);
        if (!container?.isConnected) return null;
        return createPortal(
          renderTipIcon(tip, 'top'),
          container,
          `tip-portal-${tip.id}`
        );
      })}
    </>
  );
};

